<template>
    <LoaderComponent v-if='enregistrementEnCours'/>
    <div class="modal-content" v-else>
        <h2>{{isOnlyForSite ? $t('dashboard.parametrable.edition.modale.perimetre.titre') : $t('dashboard.parametrable.modale.creation.dashboard.titre')}}</h2>
        <CreationDashboardStepsBar :step='step' v-if='!isOnlyForSite'></CreationDashboardStepsBar>
        <div class='bar-horizontale'></div>
        <input
            v-if='step === 1 && !isOnlyForSite'
            :placeholder="$t('dashboard.parametrable.modale.creation.dashboard.input.placeholder')  + ' *'"
            v-model='denominationDashboard'
            class='input' >
        <ReglePastille v-if="step === 1 && !isOnlyForSite" cle="dashboard.creationNomObligatoire" />
        <div v-else-if='sitesDisponibles.length !== 0 && initialRenderComplete' class='flex'>
            <div class='width-50pct'>
                <h4 class='margin-sites-dispo'>{{$t('dashboard.parametrable.modale.creation.dashboard.bouton.sites.disponibles')}}</h4>
                <div class="input-container-with-icon">
                    <img :src="loadIconSearch" class="icon-search" alt="search" />
                    <input
                        :placeholder="$t('dashboard.parametrable.modale.creation.dashboard.bouton.sites.recherche.placeholder')"
                        @keyup='filtrerSite'
                        v-model='filter'
                        class='input-recherche-site input'
                    >
                </div>
                <div class='demi-bar-horizontale'></div>
                <check-box
                    :checked='sitesDisponibles.length === sitesSelectionnees.length'
                    :partiel='sitesSelectionnees.length !== 0 && sitesDisponibles.length !== sitesSelectionnees.length'
                    :label="$t('dashboard.parametrable.modale.creation.dashboard.bouton.sites.tous')"
                    @click='selectionnerTousLesSites'/>
                <ReglePastille cle="dashboard.creationSitesComportement" />
                <div class='demi-bar-horizontale-2'></div>
                <div class="sites-list-container">
                    <div v-for='site in sitesDisponibles' :key='site.id'>
                        <check-box
                            :checked='isSiteSelectionnee(site.id)'
                            :label="site.description"
                            @click='ajouterSite(site)'
                            class='checkbox-site'/>
                    </div>
                </div>
            </div>
            <div class='bar-verticale width-50pct'>
                <div class='margin-left-3pct'>
                    <h4 class='margin-top-3pct'>{{$t('dashboard.parametrable.modale.creation.dashboard.perimetre.titre')}}</h4>
                    <div class='font-size-15px color-grey'>{{$t('dashboard.parametrable.modale.creation.dashboard.perimetre.description')}}</div>
                    <div class='font-size-15px color-grey margin-top-10px' v-if='isOnlyForSite'>
                        {{$t('dashboard.parametrable.edition.modale.perimetre.supplement.infos')}}
                    </div>
                </div>
                <div class='demi-bar-horizontale-right'></div>
                <div class='flex wrap sites-list-container recap-sites-list-container'>
                    <div v-for='site in sitesSelectionnees' :key='site.id' class='pastille'>
                        <span @click='ajouterSite(site)' class='cursor-pointer'>{{site.description}} X</span>
                    </div>
                </div>
            </div>
        </div>
        <div class='bar-horizontale'></div>
        <div class='boutons'>
            <div class='boutons-gauche'>
                <ButtonWhite
                    :on-click='cancel'
                    :text="$t('dashboard.parametrable.modale.creation.dashboard.bouton.annuler')"
                    class='boutonAnnuler'/>
                <ButtonWhite
                    v-if='step === 2 && !isOnlyForSite'
                    :on-click='precedent'
                    :text="$t('dashboard.parametrable.modale.creation.dashboard.bouton.precedent')"
                    class='boutonPrecedent'/>
            </div>
            <div class='boutons-droite'>
                <check-box
                    v-if='isOnlyForSite'
                    :checked='propagationWidgets'
                    :label="$t('dashboard.parametrable.edition.modale.perimetre.propagation.widgets')"
                    @click='propagationWidgets = !propagationWidgets'
                    class='checkbox-site'/>
                <ReglePastille v-if="isOnlyForSite" cle="dashboard.editionPerimetrePropagationWidgets" />
                <BoutonBleu
                    :on-click='suivant'
                    :text="textBoutonSuivant"
                    :disabled='isButtonDisabled'
                    :with-check='step === 2 && !isOnlyForSite'
                    class='boutonSuivant' :class='{ boutonCreer :step ===2, boutonEnregistrer: isOnlyForSite} '/>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import CreationDashboardStepsBar from '@/modules/Home/components/CreationDashboardStepsBar.vue';
import ButtonWhite from '@/components/ui-components/ButtonWhite.vue';
import BoutonBleu from '@/components/ui-components/BoutonBleu.vue';
import { SiteListActions } from '@/modules/SiteList/store/action-types';
import { SiteSearchDTO } from '@/modules/SiteList/service/SiteListService';
import CheckBox from '@/components/ui-components/CheckBox.vue';
import { store } from '@/plugins/store';
import { Site } from '@/models/Site';
import { HomeAction } from '@/modules/Home/store/actions-types';
import { Dashboard } from '@/models/Dashboard';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import ReglePastille from '@/components/regles/ReglePastille.vue';

interface Data {
    step: number;
    denominationDashboard: string;
    filter: string;
    sitesSelectionnees: Site[];
    initialRenderComplete: boolean;
    propagationWidgets: boolean;
}

export default defineComponent({
    name: 'ModaleCreationDashboard',
    components: { LoaderComponent, CheckBox, BoutonBleu, ButtonWhite, CreationDashboardStepsBar, ReglePastille },
    props: {
        dashboardADupliquer: { type: Object as PropType<Dashboard>, required: false },
        isOnlyForSite: { type: Boolean, required: false, default: false }
    },
    data(): Data {
        return {
            step: this.isOnlyForSite ? 2 : 1,
            denominationDashboard: '',
            filter: '',
            sitesSelectionnees: [],
            initialRenderComplete: false,
            propagationWidgets: false,
        };
    },
    async beforeMount() {
        await this.$store.dispatch(`siteList/${SiteListActions.GET_SITE_LIST}`, new SiteSearchDTO(0, this.filter, true));
        if (this.dashboardADupliquer) {
            this.denominationDashboard = store.getters.getSelectedDashboard.libelle;
            this.sitesSelectionnees = store.getters.getSelectedDashboard.sites ? store.getters.getSelectedDashboard.sites : [];
        }
        if (this.isOnlyForSite) {
            this.sitesSelectionnees = store.getters.getSelectedDashboard.sites ? store.getters.getSelectedDashboard.sites : [];
        }
        if (this.sitesSelectionnees.length === 0) {
            this.sitesSelectionnees = this.sitesDisponibles;
        }

        this.$nextTick(() => {
            this.initialRenderComplete = true;
        });
    },
    computed: {
        sitesDisponibles(): Site[] {
            return store.getters['siteList/getListValue'];
        },
        enregistrementEnCours(): boolean {
            return this.$store.getters.getEnregistrementsDashboardEnCours;
        },
        isButtonDisabled(): boolean {
            if (this.step === 1) {
                return this.denominationDashboard.trim() === '';
            }
            return false;
        },
        textBoutonSuivant(): string {
            if (this.isOnlyForSite) {
                return this.$t('dashboard.parametrable.edition.modale.perimetre.bouton.enregistrer');
            }
            return  this.step !== 1 ? this.$t('dashboard.parametrable.modale.creation.dashboard.bouton.creer') : this.$t('dashboard.parametrable.modale.creation.dashboard.bouton.suivant');
        },
        loadIconSearch() {
            return require('@/assets/img/icons/search.png');
        },
    },
    methods: {
        suivant(): void {
            if (this.step === 1) {
                this.step++;
            } else {
                const dashboard: Dashboard = {
                    id: this.isOnlyForSite ? store.getters.getSelectedDashboard.id : undefined,
                    libelle: this.isOnlyForSite ? store.getters.getSelectedDashboard.libelle : this.denominationDashboard,
                    sites: this.sitesSelectionnees,
                    propagationWidgets: this.propagationWidgets,
                    favori: this.isOnlyForSite ? store.getters.getSelectedDashboard.favori : false,
                    widgets: this.isOnlyForSite ? store.getters.getSelectedDashboard.widgets: [],
                    idDashboardDuplique: this.dashboardADupliquer ? this.dashboardADupliquer.id : undefined,
                };
                store.dispatch(HomeAction.SET_DASHBOARD, dashboard).then(() => {
                    if (this.dashboardADupliquer != null) {
                        this.$emit('duplication-dashboard-success');
                    } else {
                        this.$emit('creation-dashboard-success');
                    }
                });
            }
        },
        precedent(): void {
            this.step--;
        },
        cancel(): void {
            this.$emit('cancel');
        },
        isSiteSelectionnee(siteId: number): boolean {
            return this.sitesSelectionnees.filter(s => s.id === siteId).length > 0;
        },
        selectionnerTousLesSites(): void {
            if(this.sitesSelectionnees.length !== 0) {
                this.sitesSelectionnees = [];
            } else {
                this.sitesSelectionnees = this.sitesDisponibles;
            }
        },
        ajouterSite(site: Site): void {
            if (this.sitesSelectionnees.filter(s => s.id === site.id).length > 0) {
                this.sitesSelectionnees = this.sitesSelectionnees.filter(s => s.id !== site.id);
            } else {
                this.sitesSelectionnees.push(site);
            }
        },
        filtrerSite(): void {
            this.$store.dispatch(`siteList/${SiteListActions.GET_SITE_LIST}`, new SiteSearchDTO(0, this.filter, true));
        }
    }
});
</script>

<style scoped>
.modal-content {
    background: white;
    padding: 20px;
    border-radius: 10px;
    text-align: left;
    width: 50vw;
    max-height: 75vh;
}
.bar-horizontale {
    border-bottom: 1px solid #ccc;
    padding-bottom: 10px;
    margin-bottom: 10px;
    margin-left: -20px;
    margin-right: -20px;
}
.demi-bar-horizontale {
    border-bottom: 1px solid #ccc;
    padding-bottom: 10px;
    margin-bottom: 10px;
    margin-left: -20px;
}
.demi-bar-horizontale-2 {
    border-bottom: 1px solid #ccc;
    padding-bottom: 10px;
    margin-bottom: 10px;
    margin-right: 20px;
}
.demi-bar-horizontale-right {
    border-bottom: 1px solid #ccc;
    padding-bottom: 10px;
    margin-bottom: 10px;
    margin-right: -20px;
}
.bar-verticale {
    border-left: 1px solid #ccc;
    margin-top: -1%;
    margin-bottom: -1%;
}
:deep(.step-bar-container) {
    padding: 16px 0;
}
.input-container-with-icon {
    position: relative;
    width: 90%;
}
.icon-search {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    height: 20px;
    width: 20px;
    z-index: 1;
}
.input-recherche-site {
    width: 100% !important;
    padding-left: 35px !important;
    padding-right: 10px;
}
.input {
    width: 95%;
    padding: 10px 0 10px 10px;
    border: 1px solid var(--bleu-clair);
    border-radius: 5px;
    margin-top: 10px;
    margin-bottom: 10px;
}
.boutonAnnuler {
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border-radius: 4px;
    padding: 10px;
    font-weight: 600;
    max-height: 50px;
    width: 120px;
    border: none;
}
.boutonPrecedent {
    display: flex;
    color: var(--bleu-clair);
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border: 1px solid var(--bleu-clair);
    border-radius: 4px;
    padding: 10px;
    font-weight: 600;
    max-height: 50px;
    width: 120px;
}
.boutonSuivant {
    width: 120px;
    justify-content: center;
}
.boutonCreer {
    width: 170px;
}
.boutonEnregistrer {
    width: 120px;
}
.boutons {
    display: flex;
    justify-content: space-between;
}
.boutons-gauche {
    display: flex;
    gap: 10px;
}
.boutons-droite {
    display: flex;
    align-items: center;
    gap: 10px;
}
:deep(.step-number-container-valid) {
    background-color: var(--bleu-clair);
}
.checkbox-site {
    margin-bottom: 10px;
}
.width-50pct {
    width: 50%;
}
.margin-top-3pct {
    margin-top: 3%;
}
.margin-left-3pct {
    margin-left: 3%;
}
.font-size-15px {
    font-size: 15px;
}
.pastille {
    margin: 5px;
    background-color: var(--background);
    border-radius: 20px;
    padding: 6px;
}
.wrap {
    flex-wrap: wrap;
}
.margin-sites-dispo {
    margin-top: 1%;
    margin-bottom: 2%;
}
.color-grey {
    color: grey;
}
.sites-list-container {
    max-height: 35vh;
    overflow-y: auto;
    margin-right: 20px;
    padding-right: 10px;
}
@media screen and (max-height: 850px) {
    .sites-list-container{
        max-height: 20vh;
    }
}
.recap-sites-list-container {
    max-height: 40vh;
}
@media screen and (max-height: 850px) {
    .recap-sites-list-container{
        max-height: 29vh;
    }
}
</style>


